import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import { Helmet } from 'react-helmet';

const Section = ({ title, children }) => (
  <Box component="section" sx={{ mb: 4 }}>
    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography component="div" variant="body1" color="text.secondary" sx={{ '& p': { mb: 1.5 } }}>
      {children}
    </Typography>
  </Box>
);

const PrivacyPolicy = () => {
  const lastUpdated = 'March 28, 2026';

  return (
    <>
      <Helmet>
        <title>Privacy Policy | BookMyOP</title>
        <meta
          name="description"
          content="How BookMyOP collects, uses, and protects your information when you search for doctors, book procedures, and use our healthcare platform."
        />
      </Helmet>

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: 2 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last updated: {lastUpdated}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            BookMyOP (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates a platform that helps you discover healthcare providers,
            compare options, and book surgeries and related services. This Privacy Policy explains how we collect, use, disclose,
            and safeguard information when you use our websites, mobile applications, and related services (collectively, the
            &quot;Services&quot;).
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            By using the Services, you agree to this Privacy Policy. If you do not agree, please do not use the Services.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Section title="1. Information we collect">
            <p>
              <strong>Account and profile.</strong> When you register or update your profile, we may collect your name, email address,
              phone number, date of birth, gender (if you choose to provide it), and other details you submit.
            </p>
            <p>
              <strong>Booking and clinical context.</strong> To help you book consultations or procedures, we may collect appointment
              preferences, procedure or specialty interests, medical history summaries you choose to share, insurance or payment
              preferences, and messages you send to providers or our support team.
            </p>
            <p>
              <strong>Location.</strong> With your permission, we may collect approximate or precise location (for example to show
              nearby hospitals and doctors). You can disable location access in your device settings.
            </p>
            <p>
              <strong>Device and usage data.</strong> We collect technical information such as device type, operating system, app version,
              IP address, and how you interact with the Services (pages viewed, searches, taps, and crash logs) to improve performance
              and security.
            </p>
            <p>
              <strong>Communications.</strong> If you contact us by email, chat, or phone, we keep those messages and related metadata to
              resolve issues and improve support.
            </p>
          </Section>

          <Section title="2. How we use your information">
            <p>We use the information above to:</p>
            <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
              <li>Create and manage your account and authenticate you.</li>
              <li>Facilitate bookings, reminders, and coordination with hospitals, clinics, and doctors listed on our platform.</li>
              <li>Show relevant providers, specialties, and offers based on your preferences and location (where allowed).</li>
              <li>Send transactional messages (e.g. booking confirmations, schedule changes) and, where you opt in, marketing updates.</li>
              <li>Detect fraud, abuse, and security incidents; comply with legal obligations.</li>
              <li>Analyze usage in aggregate to improve features, reliability, and user experience.</li>
            </Box>
          </Section>

          <Section title="3. Health information">
            <p>
              Some information you provide may be considered sensitive or health-related under applicable laws. We treat such data with
              additional care and use it only as described in this policy and as needed to provide the Services you request, unless we
              obtain your consent for other uses or are required otherwise by law.
            </p>
          </Section>

          <Section title="4. Sharing of information">
            <p>We may share information:</p>
            <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1 } }}>
              <li>
                <strong>With providers and facilities</strong> you choose to book with, so they can confirm appointments and deliver care.
              </li>
              <li>
                <strong>With service providers</strong> who assist us (hosting, analytics, customer support, payments, SMS/email delivery),
                under contracts that require them to protect your data and use it only for the services they perform for us.
              </li>
              <li>
                <strong>For legal reasons</strong> if we believe disclosure is required by law, regulation, court order, or to protect
                the rights, safety, and security of our users, the public, or BookMyOP.
              </li>
              <li>
                <strong>In a business transfer</strong> such as a merger, acquisition, or sale of assets, subject to appropriate safeguards.
              </li>
            </Box>
            <p>We do not sell your personal information to third parties for their independent marketing without your consent where such consent is required.</p>
          </Section>

          <Section title="5. Cookies and similar technologies">
            <p>
              We use cookies, local storage, and similar technologies to remember your preferences, keep you signed in, measure traffic,
              and improve the Services. You can control cookies through your browser settings; disabling some cookies may limit certain
              features.
            </p>
          </Section>

          <Section title="6. Data retention">
            <p>
              We retain information for as long as your account is active and as needed to provide the Services, comply with legal
              obligations, resolve disputes, and enforce our agreements. Retention periods may vary depending on the type of data and
              applicable law.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We implement administrative, technical, and organizational measures designed to protect your information. No method of
              transmission over the internet is completely secure; we cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="8. Your choices and rights">
            <p>
              Depending on where you live, you may have rights to access, correct, delete, or export your personal data, or to object to
              or restrict certain processing. You may update some information in your account settings or contact us using the details
              below. We will respond in line with applicable law.
            </p>
            <p>
              To request deletion of your account and associated data, follow the steps on our{' '}
              <Link component={RouterLink} to="/delete-account">
                Delete account
              </Link>{' '}
              page.
            </p>
          </Section>

          <Section title="9. Children">
            <p>
              The Services are not directed at children under the age required by applicable law to consent to data collection without
              parental authorization. We do not knowingly collect personal information from such children. If you believe we have, please
              contact us and we will take appropriate steps.
            </p>
          </Section>

          <Section title="10. International transfers">
            <p>
              If you use the Services from outside the country where our servers or partners operate, your information may be processed
              in countries that may have different data protection laws. Where required, we use appropriate safeguards for cross-border
              transfers.
            </p>
          </Section>

          <Section title="11. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. We will post the revised version on this page and update the
              &quot;Last updated&quot; date. For material changes, we may provide additional notice (for example, by email or in-app
              notification). Continued use of the Services after changes means you accept the updated policy.
            </p>
          </Section>

          <Section title="12. Contact us">
            <p>
              For privacy-related questions, requests, or complaints, contact us at{' '}
              <Link href="mailto:support@easyhealthdigital.com">support@easyhealthdigital.com</Link>
              {' '}or via our{' '}
              <Link component={RouterLink} to="/contact">
                Contact
              </Link>{' '}
              page.
            </p>
          </Section>
        </Paper>
      </Container>
    </>
  );
};

export default PrivacyPolicy;
